import { ApolloProvider } from '@apollo/client'
import { CacheProvider } from '@emotion/react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { client, persistLocalCache } from 'api/client'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import React, { useEffect } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import { useLocation, useRoutes } from 'react-router-dom'

import { AuthProvider } from '@/contexts/FirebaseAuthContext'
import { useNotificationState } from '@/hooks/use-notification-state'
import useTheme from '@/hooks/useTheme'
import createEmotionCache from '@/utils/createEmotionCache'

import { NotificationContext } from './contexts/NotificationContext'
import { RefreshProvider } from './contexts/RefreshContext'
import { RemoteConfigProvider } from './contexts/RemoteConfigContext'
import { store } from './redux/store'
import routes from './routes'
import createTheme from './theme'

dayjs.extend(duration)

export const ApolloClientGate: React.FC = () => {
  useEffect(() => {
    persistLocalCache()
  }, [])
  return (
    <ApolloProvider client={client}>
      <App />
    </ApolloProvider>
  )
}

const clientSideEmotionCache = createEmotionCache()

function App({ emotionCache = clientSideEmotionCache }) {
  const content = useRoutes(routes)
  const notificationState = useNotificationState()
  const { theme } = useTheme()

  // This is needed to scroll to the top of the page on route change
  const location = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  return (
    <RemoteConfigProvider>
      <CacheProvider value={emotionCache}>
        <HelmetProvider>
          <Helmet defaultTitle={'Delicious AI Admin'} />
          <Provider store={store}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MuiThemeProvider theme={createTheme(theme)}>
                <NotificationContext.Provider value={notificationState}>
                  <RefreshProvider>
                    <AuthProvider>{content}</AuthProvider>
                  </RefreshProvider>
                </NotificationContext.Provider>
              </MuiThemeProvider>
            </LocalizationProvider>
          </Provider>
        </HelmetProvider>
      </CacheProvider>
    </RemoteConfigProvider>
  )
}

export default App
